import "./ProjectAssetsComponent.scss";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog.tsx";
import * as React from "react";
import { useCallback, useEffect, useState, useRef } from "react";
import commonService from "@/services/common.service.ts";
import { Button } from "@/components/ui/button.tsx";
import axios from "axios";
import { File, Image, FileSpreadsheet, Check, Upload, Circle, Trash2 } from "lucide-react";

interface ProjectAssetsComponentProps {
    projectId: string;
    onSelectAssets: (assets: Asset[]) => void;
    preSelectedAssets?: Asset[];
}

interface Asset {
    url: string;
    name: string;
    content_type: string;
    type?: string;
}

const ProjectAssetsComponent = (props: ProjectAssetsComponentProps) => {
    const { projectId, onSelectAssets, preSelectedAssets } = props;

    const [projectAssetsOpen, setProjectAssetsOpen] = useState(false);
    const [assets, setAssets] = useState<Asset[]>([]);
    const [selectedAssets, setSelectedAssets] = useState<Set<number>>(new Set());
    const fileTypes = ["image/*", "application/vnd.ms-excel", "text/csv"];
    const [isFileUploading, setIsFileUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [deletingAssets, setDeletingAssets] = useState<Set<string>>(new Set());

    useEffect(() => {
        const sub = commonService._communications.toggleFileUploadSubject.subscribe((isOpen) => {
            if (isOpen) {
                setProjectAssetsOpen((prev: boolean) => !prev);
            }
        });
        return () => {
            sub.unsubscribe();
        }
    }, []);

    const getProjectAssets = useCallback(() => {
        commonService._project_service.getProjectAssets(projectId, {}).then((response) => {
            setAssets(response?.data?.assets || []);
        }).catch((error) => {
            console.error(error);
            commonService._alert.showToast("Error loading assets", "error")
        })
    }, [projectId])

    const handleSaveAssets = useCallback((urlDetails: any, file: File) => {
        const url = urlDetails?.upload_url;
        const headers = urlDetails?.headers || {};

        axios.put(url, file, {
            headers: {
                'Content-Type': file.type,
                ...headers,
            },
        })
            .then(() => {
                setAssets((prevAssets: Asset[]) => {
                    const currentAsset: Asset = {
                        url: urlDetails?.public_url,
                        name: urlDetails.file_name,
                        content_type: urlDetails.content_type
                    }
                    if (!prevAssets) {
                        return [currentAsset]
                    }
                    return [...prevAssets, currentAsset]
                })
                commonService._alert.showToast("File uploaded successfully", "success");
            })
            .catch((error) => {
                commonService._alert.showToast(error?.detail || "Error uploading file", "error")
            })
            .finally(() => {
                setIsFileUploading(false)
            })
    }, []);

    const createImageUrl = useCallback((file: File) => {
        setIsFileUploading(true);
        const payload = {
            file_name: file.name,
            content_type: file.type,
        }
        commonService._project_service.createProjectAssetUrl(projectId, payload).then((response) => {
            handleSaveAssets(response?.data, file)
        })
            .catch((error) => {
                commonService._alert.showToast(error?.detail || "Error uploading file", "error")
                setIsFileUploading(false);
            })
    }, [projectId, handleSaveAssets])

    const handleFileUpload = (files: FileList | null) => {
        if (!files) return;

        Array.from(files).forEach(file => {
            createImageUrl(file);
        });
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        handleFileUpload(event.target.files);
        // Reset the input value so the same file can be uploaded again if needed
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // LAZY LOAD: Only fetch assets when the dialog is opened
    useEffect(() => {
        if (projectAssetsOpen) {
        getProjectAssets()
        }
    }, [projectAssetsOpen, getProjectAssets]);

    const toggleAssetSelection = (index: number) => {
        setSelectedAssets(prev => {
            const newSet = new Set(prev);
            if (newSet.has(index)) {
                newSet.delete(index);
            } else {
                newSet.add(index);
            }
            return newSet;
        });
    };

    const getFileIcon = (contentType: string) => {
        if (contentType.startsWith('image/')) {
            return <Image className="w-6 h-6 text-blue-500" />;
        } else if (contentType.includes('excel') || contentType.includes('csv')) {
            return <FileSpreadsheet className="w-6 h-6 text-green-500" />;
        }
        return <File className="w-6 h-6 text-gray-500" />;
    };

    const renderAssetPreview = (asset: Asset) => {
        if (asset.content_type.startsWith('image/')) {
            return (
                <img
                    src={asset.url}
                    alt={asset.name}
                    className="w-full h-20 object-cover rounded-t-lg"
                />
            );
        }
        return (
            <div className="w-full h-32 bg-gray-50 rounded-t-lg flex items-center justify-center">
                {getFileIcon(asset.content_type)}
            </div>
        );
    };

    useEffect(() => {
        if (preSelectedAssets) {
            const newSelectedAssets = new Set(preSelectedAssets.map((asset, index) => index));
            setSelectedAssets(newSelectedAssets);
        }
    }, [preSelectedAssets]);

    const handleDeleteAsset = async (asset: Asset, e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent selection toggle
        if (deletingAssets.has(asset.name)) return;

        setDeletingAssets(prev => new Set([...prev, asset.name]));
        try {
            await commonService._project_service.deleteProjectAsset(projectId, asset.name);
            setAssets(prev => prev.filter(a => a.name !== asset.name));
            commonService._alert.showToast("Asset deleted successfully", "success");
        } catch (error) {
            console.error(error);
            commonService._alert.showToast("Error deleting asset", "error");
        } finally {
            setDeletingAssets(prev => {
                const newSet = new Set(prev);
                newSet.delete(asset.name);
                return newSet;
            });
        }
    };

    return (
        <Dialog
            open={projectAssetsOpen}
            size={"lg"}
            fullWidth={true}
            showClose={true}
            onOpenChange={(isOpen) => {
                setProjectAssetsOpen(isOpen)
            }}

        >
            <DialogContent className="max-w-4xl">
                <DialogTitle className="text-xl font-semibold mb-2">Project Assets</DialogTitle>

                <div className="project-assets-component">

                    {/* Assets Grid */}
                    {assets?.length > 0 ? (
                        <>
                            <div className="mb-4 d-flex justify-end">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept={fileTypes.join(',')}
                                    onChange={handleFileInputChange}
                                    className="hidden"
                                    multiple
                                />
                                <Button
                                    onClick={handleUploadClick}
                                    disabled={isFileUploading}
                                    variant="default"

                                >
                                    <Upload className="w-5 h-5" />
                                    {isFileUploading ? 'Uploading...' : 'Upload Files'}
                                </Button>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                                {assets?.map((asset, index) => (
                                    <div
                                        key={index}
                                        onClick={() => toggleAssetSelection(index)}
                                        className={`
                                        relative bg-white rounded-lg shadow-sm border-2 cursor-pointer transition-all duration-200 hover:shadow-md
                                        ${selectedAssets.has(index)
                                                ? 'border-blue-500 ring-2 ring-blue-200'
                                                : 'border-gray-200 hover:border-gray-300'
                                            }
                                    `}
                                    >
                                        {/* Selection Indicator */}
                                        {selectedAssets.has(index) ? (
                                            <div className="absolute top-2 right-2 z-10 bg-blue-500 rounded-full p-1">
                                                <Check className="w-4 h-4 text-white" />
                                            </div>
                                        ) :
                                            <div className="absolute top-2 right-2 z-10  rounded-full p-1">
                                                <Circle className="w-6 h-6 text-blue-500" />
                                            </div>
                                        }

                                        {/* Add Delete Button */}
                                        <Button variant="image"  className="absolute bottom-0 right-0 z-10  rounded-full "
                                        onClick={(e) => handleDeleteAsset(asset, e)}
                                        >
                                            {deletingAssets.has(asset.name) ? (
                                                <div className="w-5 h-5 border-2 border-destructive border-t-transparent rounded-full animate-spin" />
                                            ) : (
                                                <Trash2 className="w-5 h-5 text-destructive" />
                                            )}
                                        </Button>

                                        {/* Asset Preview */}
                                        {renderAssetPreview(asset)}

                                        {/* Asset Info */}
                                        <div className="p-3">
                                            <h3 className="font-medium text-sm text-gray-900 truncate mb-1" title={asset.name}>
                                                {asset.name}
                                            </h3>
                                            <p className="text-xs text-gray-500 uppercase tracking-wide">
                                                {asset.content_type.split('/')[1] || 'File'}
                                            </p>
                                        </div>

                                        {/* Download Link */}
                                        <div className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-200">
                                            <a
                                                href={asset.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(e) => e.stopPropagation()}
                                                className="absolute bottom-2 left-2 right-2 bg-black bg-opacity-75 text-white text-xs py-1 px-2 rounded text-center hover:bg-opacity-90"
                                            >
                                                View/Download
                                            </a>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>

                    ) : (
                        <div className="text-center py-12 text-gray-500">
                            <File className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                            <p className="text-lg font-medium mb-2">No assets found</p>
                            <p className="text-sm">Upload your first asset using the button above</p>
                            <div className="mb-4 d-flex justify-center mt-4">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept={fileTypes.join(',')}
                                    onChange={handleFileInputChange}
                                    className="hidden"
                                    multiple
                                />
                                <Button
                                    onClick={handleUploadClick}
                                    disabled={isFileUploading}
                                    variant="default"

                                >
                                    <Upload className="w-5 h-5" />
                                    {isFileUploading ? 'Uploading...' : 'Upload Files'}
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Selection Actions */}
                    {selectedAssets?.size > 0 && (
                        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-white border border-gray-200 rounded-lg shadow-lg p-4 flex items-center gap-4 z-30">
                            <span className="text-sm font-medium text-gray-700">
                                {selectedAssets?.size} asset{selectedAssets?.size > 1 ? 's' : ''} selected
                            </span>
                            <div className="flex gap-2">
                                <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => setSelectedAssets(new Set())}
                                >
                                    Clear Selection
                                </Button>
                                <Button
                                    variant="default"
                                    size="sm"
                                    onClick={() => {
                                        setProjectAssetsOpen(false)
                                        onSelectAssets(assets.filter((_, index) => selectedAssets.has(index)))
                                    }}
                                >
                                    Use Selected
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ProjectAssetsComponent;
